// react
import { ChangeEvent, Dispatch, MouseEvent, SetStateAction, useContext, useState } from 'react';

// types
import { Country, Entity, Frame, User, UserPayload, UserProject } from '../../../@types';
import { entityTypesExt } from '../../../@types/unions';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// mui
import { Alert, Grid, SelectChangeEvent } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';
import { InputPhone } from '../../fields/InputPhone/InputPhone';

// ui
import { UserPermissions } from '../../ui/UserPermissions/UserPermissions';

// hooks
import { usePermissions } from '../../../hooks/usePermissions';

// utils
import { permissionsReadWrite } from '../../../utils/permissions';

// props
export interface FormEditUserProps {
	frame?: Frame;
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
	user: User;
}

export const FormEditUser = ({
	frame,
	onClose,
	onSuccess,
	setIsLoading,
	user,
}: FormEditUserProps) => {
	// hooks
	const { t } = useTranslation();
	const { filterPermissions } = usePermissions();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// defaults
	const userPermissions = filterPermissions(
		(user?.projects as UserProject[]).find((p) => p.id === project?.id)?.permissions ||
			permissionsReadWrite
	);

	// state
	const [error, setError] = useState<string | null>(null);
	const [values, setValues] = useState({
		country: user.country ? String((user.country as Country)?.id) : '',
		email: user.email,
		firstName: user.firstName,
		lastName: user.lastName,
		permissions: userPermissions,
		phone: String(user.phone || ''),
		title: user.title,
	});

	// vars
	const idForm = `form-edit-user`;
	let isEntityTypeExt = false;

	for (let u = 0; u < user.entities.length; u++) {
		if (entityTypesExt.some((e) => e === (user.entities[u] as Entity).type)) {
			isEntityTypeExt = true;
			break;
		}
	}

	const isOnlyExt =
		user.entities.length > 0 &&
		user.entities.every((e) => entityTypesExt.some((et) => et === (e as Entity).type));
	const hasExt = user.entities.some((e) => entityTypesExt.some((et) => et === (e as Entity).type));
	const isMixedExt = hasExt && !isOnlyExt;
	const canEditEmail = isOnlyExt;
	const canEditPhone = isOnlyExt || isMixedExt;

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		// Either an email or a phone number is required — but not both.
		// Mirror the rule enforced in FormAddUser (and on the backend via
		// USER_PHONE_OR_EMAIL_EMPTY) so a user can't clear both fields and save.
		const trimmedEmail = String(values.email || '').trim();
		const phone = String(values.phone || '').replace(/\D/g, '');
		if (!trimmedEmail && !phone) {
			setError(t('error.userEmailOrPhoneRequired'));
			return;
		}

		// Create the body object with the required fields
		const body: Partial<UserPayload> = {
			firstName: values.firstName,
			lastName: values.lastName,
		};

		body.phone = phone ? Number(phone) : undefined;

		// Only send `email` when the form actually has a value. The backend
		// treats a missing email as "no change", but sending an empty/blank
		// email would either fail validation or, worse, overwrite the stored
		// email with NULL. Never PATCH away an existing email.
		if (trimmedEmail) {
			body.email = trimmedEmail;
		}

		if (values.title) {
			body.title = values.title;
		}

		if (!isEntityTypeExt) {
			body.projects = [
				{
					id: project?.id,
					permissions: values.permissions,
				},
			];
		}

		try {
			// set loader
			setIsLoading(true);

			const fetchEditUser = await fetch(`${process.env.REACT_APP_API_URL}/users/${user.id}`, {
				method: 'PATCH',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify(body),
			});
			const resEditUser = await fetchEditUser.json();

			if (resEditUser.error) {
				throw new Error(resEditUser.error.message, {
					cause: {
						id: resEditUser.error.id,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.userEdited', { name: `${values.firstName} ${values.lastName}` }),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const message = t('error.default');

			// set error
			setError(message);
		} finally {
			// set loader
			setIsLoading(false);
		}
	};

	return (
		<form id={idForm} name="formEditUser" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-first-name`}
						label={t('firstName')}
						name="firstName"
						onChange={handleChangeValue}
						required={true}
						value={values.firstName}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-last-name`}
						label={t('lastName')}
						name="lastName"
						onChange={handleChangeValue}
						required={true}
						value={values.lastName}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-email`}
						label={t('email')}
						name="email"
						onChange={handleChangeValue}
						type="email"
						value={values.email}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<InputPhone
						InputProps={{
							id: `${idForm}-phone`,
							onChange: handleChangeValue,
							value: values.phone,
						}}
						label={t('phone')}
						SelectProps={{
							disabled: true,
							id: `${idForm}-code`,
							onChange: handleChangeValue,
							value: values.country,
						}}
					/>
				</Grid>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-title`}
						label={t('jobTitle')}
						name="title"
						onChange={handleChangeValue}
						value={values.title}
					/>
				</Grid>
				{!isEntityTypeExt && (
					<Grid item xs={12}>
						<UserPermissions
							defaultPermissions={userPermissions}
							defaultTemplate="CUSTOM"
							onChange={(e, permissions) => {
								setValues((prev) => ({
									...prev,
									permissions,
								}));
							}}
						/>
					</Grid>
				)}
				{error && (
					<Grid item xs={12}>
						<Alert severity="error">{error}</Alert>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
